import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import api from '@/lib/api';
import type { SupportFaqAdminResponse, SupportCategoryAdminResponse } from '@/lib/api';

interface FaqFormData {
  question: string;
  answer: string;
  categoryId: string;
  displayOrder: number;
  isPublished: boolean;
}

const DEFAULT_FORM: FaqFormData = {
  question: '',
  answer: '',
  categoryId: '',
  displayOrder: 0,
  isPublished: true,
};

export function AdminFaqPage() {
  const [faqs, setFaqs] = useState<SupportFaqAdminResponse[]>([]);
  const [categories, setCategories] = useState<SupportCategoryAdminResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFaq, setEditingFaq] = useState<SupportFaqAdminResponse | null>(null);
  const [form, setForm] = useState<FaqFormData>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [faqRes, catRes] = await Promise.all([
        api.listSupportFaqsAdmin(),
        api.listSupportCategoriesAdmin(),
      ]);
      setFaqs(faqRes.faqs);
      setCategories(catRes.categories);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () => {
    setEditingFaq(null);
    setForm({
      ...DEFAULT_FORM,
      displayOrder: faqs.length > 0 ? Math.max(...faqs.map((f) => f.displayOrder)) + 1 : 1,
    });
    setShowDialog(true);
  };

  const openEdit = (faq: SupportFaqAdminResponse) => {
    setEditingFaq(faq);
    setForm({
      question: faq.question,
      answer: faq.answer,
      categoryId: faq.categoryId || '',
      displayOrder: faq.displayOrder,
      isPublished: faq.isPublished,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingFaq) {
        await api.updateSupportFaq(editingFaq.faqId, {
          question: form.question,
          answer: form.answer,
          categoryId: form.categoryId || undefined,
          displayOrder: form.displayOrder,
          isPublished: form.isPublished,
        });
      } else {
        await api.createSupportFaq({
          question: form.question,
          answer: form.answer,
          categoryId: form.categoryId || undefined,
          displayOrder: form.displayOrder,
          isPublished: form.isPublished,
        });
      }
      setShowDialog(false);
      fetchData();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faqId: string) => {
    setDeleting(faqId);
    try {
      await api.deleteSupportFaq(faqId);
      fetchData();
    } finally {
      setDeleting(null);
    }
  };

  const handleReorder = async (faqId: string, direction: 'up' | 'down') => {
    const idx = faqs.findIndex((f) => f.faqId === faqId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= faqs.length) return;

    const currentOrder = faqs[idx].displayOrder;
    const swapOrder = faqs[swapIdx].displayOrder;

    await Promise.all([
      api.updateSupportFaq(faqs[idx].faqId, { displayOrder: swapOrder }),
      api.updateSupportFaq(faqs[swapIdx].faqId, { displayOrder: currentOrder }),
    ]);
    fetchData();
  };

  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return '—';
    return categories.find((c) => c.categoryId === categoryId)?.name || '—';
  };

  const sortedFaqs = [...faqs].sort((a, b) => a.displayOrder - b.displayOrder);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">FAQ Management</h2>
          <p className="text-sm text-muted-foreground">
            {faqs.length} FAQ{faqs.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Question</TableHead>
              <TableHead className="w-32">Category</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24">Order</TableHead>
              <TableHead className="w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedFaqs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No FAQs yet. Click "Add FAQ" to create one.
                </TableCell>
              </TableRow>
            ) : (
              sortedFaqs.map((faq, idx) => (
                <TableRow key={faq.faqId}>
                  <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                  <TableCell className="max-w-md">
                    <div className="truncate font-medium">{faq.question}</div>
                    <div className="truncate text-xs text-muted-foreground mt-0.5">
                      {faq.answer.substring(0, 100)}...
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{getCategoryName(faq.categoryId)}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={faq.isPublished ? 'default' : 'secondary'}>
                      {faq.isPublished ? 'Published' : 'Draft'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={idx === 0}
                        onClick={() => handleReorder(faq.faqId, 'up')}
                      >
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        disabled={idx === sortedFaqs.length - 1}
                        onClick={() => handleReorder(faq.faqId, 'down')}
                      >
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(faq)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={deleting === faq.faqId}
                        onClick={() => handleDelete(faq.faqId)}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editingFaq ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
            <DialogDescription>
              {editingFaq
                ? 'Update the FAQ entry details.'
                : 'Create a new FAQ entry for the support page.'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Question</Label>
                <Input
                  value={form.question}
                  onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                  placeholder="What is the question?"
                  maxLength={1000}
                />
              </div>

              <div className="space-y-2">
                <Label>Answer</Label>
                <Textarea
                  value={form.answer}
                  onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                  placeholder="Write the answer (supports markdown)..."
                  rows={8}
                  maxLength={10000}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={form.categoryId || 'none'}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, categoryId: v === 'none' ? '' : v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No category</SelectItem>
                      {categories
                        .filter((c) => c.isActive)
                        .map((c) => (
                          <SelectItem key={c.categoryId} value={c.categoryId}>
                            {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={form.displayOrder}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, displayOrder: parseInt(e.target.value, 10) || 0 }))
                    }
                    min={0}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  checked={form.isPublished}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, isPublished: checked }))}
                />
                <Label>Published</Label>
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.question.trim() || !form.answer.trim()}
            >
              {saving ? 'Saving...' : editingFaq ? 'Update FAQ' : 'Create FAQ'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
